// ─────────────────────────────────────────────────────────────────────────────
// Promo status hook — shared by the scarcity banner, header pill, and any
// signup surface that needs to react to remaining spots.
//
// Polls /api/promo/status/{code} on mount + every 30 s while the page is
// visible. `visibilitychange` handling stops the poll when the tab is
// backgrounded so we're not hammering the API from 40 idle tabs.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;

export const usePromoStatus = (code) => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/promo/status/${code}`);
        if (!cancelled) {
          setStatus(res.data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err);
      }
    };

    const startPolling = () => {
      fetchStatus();
      intervalRef.current = setInterval(fetchStatus, POLL_MS);
    };
    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    const onVisibilityChange = () => {
      if (document.hidden) stopPolling();
      else if (!intervalRef.current) startPolling();
    };

    if (!document.hidden) startPolling();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [code]);

  return { status, error, refresh: () => setStatus(null) };
};

export default usePromoStatus;
